/**
 * Raw stroke → snapped wall chain.
 *
 * The designer draws a rough perimeter freehand; this turns it into segments
 * snapped to the allowed angle increments. Two passes, in this order:
 *
 *   1. Ramer–Douglas–Peucker simplification. Best when the drawing has clear
 *      corners, which is the common case.
 *   2. A direction-change scan, used when RDP finds too few corners — e.g. a
 *      slow, wobbly stroke where no single point is far enough from the chord
 *      to survive RDP.
 *
 * IMPORTANT: cleanup DISCARDS the raw stroke. Once the geometry is captured as
 * walls the stroke has done its job, and keeping it would leave two
 * representations of the same edge on screen, drifting apart as walls are
 * edited. Confirmed as intended behavior by the project owner (2026-07-22);
 * the reference prototype already did this.
 *
 * Ported from reference/bluetooth-room-sketch-prototype/index.html.
 */
import { angleDelta, radToDeg, snapAngle, toCvAngle } from './angles.js';
import { distance, projectPoint } from './vector.js';

/** Points closer together than this add nothing but noise. */
const STROKE_RESAMPLE_PX = 5;

/** Shorter than this is a wobble, not a wall. */
const DEFAULT_MIN_SEGMENT_PX = 24;

/** RDP tolerance — how far a point must sit off the chord to count as a corner. */
const DEFAULT_RDP_EPSILON_PX = 16;

/** Endpoints closer than this mean the designer meant to close the loop. */
const CLOSE_LOOP_PX = 42;

const pointsFormClosedLoop = (points) => (
  points.length > 8 && distance(points[0], points[points.length - 1]) < CLOSE_LOOP_PX
);

/**
 * True when a freehand stroke ends close enough to its start that Cleanup
 * should treat it as an intended room perimeter. This is topology intent, not
 * solved geometry: snapping each edge to 45° can move the final endpoint away
 * again, and the closure solver is responsible for reconciling that variance.
 */
export function strokeFormsClosedLoop(stroke) {
  return pointsFormClosedLoop(resampleStroke(stroke));
}

/** Thins a stroke to points at least STROKE_RESAMPLE_PX apart. */
export function resampleStroke(stroke) {
  const kept = [];
  for (const point of stroke) {
    if (!kept.length || distance(point, kept[kept.length - 1]) >= STROKE_RESAMPLE_PX) {
      kept.push({ x: point.x, y: point.y });
    }
  }
  return kept;
}

/** Perpendicular distance from `p` to segment `a`–`b` (clamped to the segment). */
export function perpendicularDistance(p, a, b) {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  if (dx === 0 && dy === 0) return distance(p, a);
  const t = Math.max(0, Math.min(1, ((p.x - a.x) * dx + (p.y - a.y) * dy) / (dx * dx + dy * dy)));
  return distance(p, { x: a.x + t * dx, y: a.y + t * dy });
}

/** Ramer–Douglas–Peucker polyline simplification. */
export function rdp(points, epsilon = DEFAULT_RDP_EPSILON_PX) {
  if (points.length <= 2) return points.slice();

  let maxDistance = 0;
  let index = 0;
  for (let i = 1; i < points.length - 1; i++) {
    const d = perpendicularDistance(points[i], points[0], points[points.length - 1]);
    if (d > maxDistance) {
      maxDistance = d;
      index = i;
    }
  }

  if (maxDistance > epsilon) {
    const left = rdp(points.slice(0, index + 1), epsilon);
    const right = rdp(points.slice(index), epsilon);
    return left.slice(0, -1).concat(right);
  }
  return [points[0], points[points.length - 1]];
}

/**
 * Extracts corner points from one raw stroke.
 *
 * @param {{x:number,y:number}[]} stroke
 * @param {object} [options]
 * @param {number} [options.snapIncrementDegrees] the snap_increment_degrees rule
 * @param {number} [options.minSegmentPx]
 * @param {number} [options.rdpEpsilonPx]
 */
export function cornersFromStroke(stroke, options = {}) {
  const snapIncrement = options.snapIncrementDegrees ?? 45;
  const minSegment = options.minSegmentPx ?? DEFAULT_MIN_SEGMENT_PX;
  const epsilon = options.rdpEpsilonPx ?? DEFAULT_RDP_EPSILON_PX;

  const points = resampleStroke(stroke);
  if (points.length < 2) return [];

  const isClosedLoop = pointsFormClosedLoop(points);

  // Pass 1 — RDP, preferred when it finds real corners.
  const rdpCorners = rdp(points, epsilon).filter((p, i, arr) => i === 0 || distance(p, arr[i - 1]) >= 6);
  if (rdpCorners.length >= 3) {
    if (isClosedLoop && distance(rdpCorners[0], rdpCorners[rdpCorners.length - 1]) > CLOSE_LOOP_PX) {
      rdpCorners.push({ ...rdpCorners[0] });
    }
    return rdpCorners;
  }

  // Pass 2 — direction-change scan.
  const corners = [points[0]];
  let anchor = points[0];
  let currentSnap = null;
  let lastPoint = points[0];

  for (let i = 1; i < points.length; i++) {
    const point = points[i];
    if (distance(anchor, point) < minSegment) {
      lastPoint = point;
      continue;
    }

    const snap = snapAngle(radToDeg(Math.atan2(point.y - anchor.y, point.x - anchor.x)), snapIncrement);

    if (currentSnap === null) {
      currentSnap = snap;
      lastPoint = point;
      continue;
    }

    // A corner needs BOTH a real direction change and enough wall on either
    // side of it — otherwise a shaky hand produces a corner every few pixels.
    const enoughPriorWall = distance(anchor, lastPoint) >= minSegment * 1.4;
    const enoughNewDirection = distance(lastPoint, point) >= 10;
    if (angleDelta(snap, currentSnap) >= snapIncrement && enoughPriorWall && enoughNewDirection) {
      const corner = projectPoint(anchor, lastPoint, currentSnap);
      if (distance(corner, corners[corners.length - 1]) >= minSegment) corners.push(corner);
      anchor = corner;
      currentSnap = snapAngle(radToDeg(Math.atan2(point.y - anchor.y, point.x - anchor.x)), snapIncrement);
    }
    lastPoint = point;
  }

  if (currentSnap !== null && distance(anchor, lastPoint) >= minSegment) {
    corners.push(projectPoint(anchor, lastPoint, currentSnap));
  }

  if (isClosedLoop && corners.length > 2) {
    const last = corners[corners.length - 1];
    if (distance(last, corners[0]) < 60) corners[corners.length - 1] = { ...corners[0] };
    else corners.push({ ...corners[0] });
  }

  // The direction-change pass can be too conservative on a smooth curve.
  if (corners.length < 2) return rdp(points, epsilon);
  return corners;
}

/**
 * Converts raw strokes into a snapped wall chain.
 *
 * Each returned segment carries BOTH representations: `internalAngle` (0–359,
 * for trig and rendering) and `angleDeg` (Cabinet Vision signed, the only value
 * that may be stored). See ADR 0004 — keeping them together here means the
 * conversion happens once, at the point of creation, rather than being
 * re-derived by every consumer.
 *
 * @param {{x:number,y:number}[][]} strokes
 * @param {object} [options]
 * @param {() => string} [options.makeId] id factory (injected so tests are deterministic)
 */
export function strokesToWalls(strokes, options = {}) {
  const snapIncrement = options.snapIncrementDegrees ?? 45;
  const minSegment = options.minSegmentPx ?? DEFAULT_MIN_SEGMENT_PX;
  const makeId = options.makeId ?? (() => crypto.randomUUID());

  const walls = [];

  for (const stroke of strokes) {
    const corners = cornersFromStroke(stroke, options).filter(
      (p, i, arr) => i === 0 || distance(p, arr[i - 1]) >= 6,
    );
    if (corners.length < 2) continue;

    let cursor = { ...corners[0] };
    for (let i = 1; i < corners.length; i++) {
      const rawEnd = corners[i];
      const length = distance(cursor, rawEnd);
      if (length < minSegment) continue;

      const rawAngle = radToDeg(Math.atan2(rawEnd.y - cursor.y, rawEnd.x - cursor.x));
      const internalAngle = snapAngle(rawAngle, snapIncrement);
      const end = projectPoint(cursor, rawEnd, internalAngle);

      walls.push({
        id: makeId(),
        start: { ...cursor },
        end,
        internalAngle,
        angleDeg: toCvAngle(internalAngle),
        rawAngle: Math.round(rawAngle),
        segmentKind: 'wall',
        isMeasured: false,
        lengthUnits: null,
        includeInElevation: true,
      });
      cursor = end;
    }
  }

  return walls;
}
